// Lightweight checklist for a project task (tasks-clickup-overhaul P2).
// Distinct from subtasks: checkbox steps with a progress bar. Self-contained —
// loads + mutates via the checklist store, holds its own local list state.

import { useEffect, useState } from "preact/hooks";
import { Check, Plus, Trash2 } from "lucide-preact";
import type { TaskChecklistItem } from "@juuplan/shared";
import {
  addChecklistItem,
  deleteChecklistItem,
  fetchChecklist,
  patchChecklistItem,
} from "../store/checklist";
import { toastError } from "../lib/toast";

interface Props {
  taskId: number;
  readOnly?: boolean;
}

export function ChecklistSection(props: Props): preact.JSX.Element {
  const { taskId, readOnly = false } = props;
  const [items, setItems] = useState<TaskChecklistItem[]>([]);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState("");

  // Der leere Reject-Handler `() => {}` machte einen fehlgeschlagenen Abruf
  // von einer leeren Checkliste ununterscheidbar.
  const [loadError, setLoadError] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const reload = (): void => setReloadTick((t) => t + 1);

  useEffect(() => {
    let cancelled = false;
    setLoadError(false);
    void fetchChecklist(taskId).then(
      (rows) => {
        if (!cancelled) setItems(rows ?? []);
      },
      () => {
        if (cancelled) return;
        setLoadError(true);
        toastError("Checkliste konnte nicht geladen werden.");
      }
    );
    return () => {
      cancelled = true;
    };
    // `reloadTick` haengt drin, damit „Erneut laden" den Abruf neu startet.
  }, [taskId, reloadTick]);

  const done = items.filter((i) => i.is_done).length;
  const total = items.length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  const add = async (): Promise<void> => {
    const trimmed = title.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    try {
      const item = await addChecklistItem(taskId, trimmed);
      setItems((prev) => [...prev, item]);
      setTitle("");
    } catch {
      toastError("Schritt konnte nicht hinzugefügt werden");
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (item: TaskChecklistItem): Promise<void> => {
    // optimistic
    setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, is_done: !i.is_done } : i)));
    try {
      await patchChecklistItem(taskId, item.id, { is_done: !item.is_done });
    } catch {
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, is_done: item.is_done } : i)));
      toastError("Änderung fehlgeschlagen");
    }
  };

  const startEdit = (item: TaskChecklistItem): void => {
    if (readOnly) return;
    setEditingId(item.id);
    setEditValue(item.title);
  };

  const commitEdit = async (item: TaskChecklistItem): Promise<void> => {
    const trimmed = editValue.trim();
    setEditingId(null);
    if (!trimmed || trimmed === item.title) return;
    // optimistic
    setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, title: trimmed } : i)));
    try {
      await patchChecklistItem(taskId, item.id, { title: trimmed });
    } catch {
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, title: item.title } : i)));
      toastError("Umbenennen fehlgeschlagen");
    }
  };

  const remove = async (item: TaskChecklistItem): Promise<void> => {
    const prev = items;
    setItems((p) => p.filter((i) => i.id !== item.id));
    try {
      await deleteChecklistItem(taskId, item.id);
    } catch {
      setItems(prev);
      toastError("Löschen fehlgeschlagen");
    }
  };

  return (
    <div>
      <h3 class="text-xs font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)] mb-2 flex items-center gap-2">
        Checkliste
        {total > 0 && (
          <span class="text-label font-normal tabular-nums text-[color:var(--color-on-surface-variant)]">
            {done}/{total}
          </span>
        )}
      </h3>

      {total > 0 && (
        <div class="mb-2 h-1.5 rounded-full bg-[color:var(--color-surface-container-high)] overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
          <div class="h-full bg-[color:var(--color-status-done)] transition-[width]" style={{ width: `${pct}%` }} />
        </div>
      )}

      {loadError && (
        <p class="mb-2 text-xs text-[color:var(--color-error)]">
          Checkliste konnte nicht geladen werden.{" "}
          <button type="button" onClick={reload} class="underline">Erneut laden</button>
        </p>
      )}

      <ul class="flex flex-col gap-0.5">
        {items.map((item) => (
          <li key={item.id} class="group flex items-center gap-2 py-0.5">
            {/* 28px hit area around the visual 16px box (touch target). */}
            <button
              type="button"
              onClick={() => void toggle(item)}
              disabled={readOnly}
              class="shrink-0 inline-flex items-center justify-center size-7 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] disabled:cursor-default"
              aria-label={item.is_done ? `${item.title} als offen markieren` : `${item.title} als erledigt markieren`}
              aria-pressed={item.is_done}
            >
              <span
                class="inline-flex size-4 items-center justify-center rounded border border-[color:var(--color-outline)] text-[color:var(--color-on-primary)]"
                style={item.is_done ? { background: "var(--color-status-done)", borderColor: "var(--color-status-done)" } : undefined}
              >
                {item.is_done && <Check size={14} />}
              </span>
            </button>
            {editingId === item.id ? (
              <input
                type="text"
                value={editValue}
                autoFocus
                onInput={(e) => setEditValue((e.target as HTMLInputElement).value)}
                onBlur={() => void commitEdit(item)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    (e.target as HTMLInputElement).blur();
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    setEditingId(null);
                  }
                }}
                maxLength={500}
                aria-label={`„${item.title}" umbenennen`}
                class="flex-1 min-w-0 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-1.5 py-0.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
              />
            ) : (
              <span
                onDblClick={() => startEdit(item)}
                title={readOnly ? undefined : "Doppelklick zum Bearbeiten"}
                class={`flex-1 min-w-0 text-sm ${readOnly ? "" : "cursor-text"} ${item.is_done ? "line-through opacity-60" : "text-[color:var(--color-on-surface)]"}`}
              >
                {item.title}
              </span>
            )}
            {!readOnly && (
              <button
                type="button"
                onClick={() => void remove(item)}
                class="shrink-0 inline-flex items-center justify-center size-6 rounded text-[color:var(--color-on-surface-variant)] opacity-100 md:opacity-0 md:group-hover:opacity-100 hover:text-[color:var(--color-error)] focus:outline-none focus:opacity-100"
                aria-label={`„${item.title}" löschen`}
              >
                <Trash2 size={14} aria-hidden="true" />
              </button>
            )}
          </li>
        ))}
      </ul>

      {!readOnly && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void add();
          }}
          class="mt-1.5 flex items-center gap-2"
        >
          <Plus size={14} class="text-[color:var(--color-on-surface-variant)] shrink-0" aria-hidden="true" />
          <input
            type="text"
            value={title}
            onInput={(e) => setTitle((e.target as HTMLInputElement).value)}
            placeholder="Schritt hinzufügen…"
            aria-label="Neuer Checklisten-Schritt"
            maxLength={500}
            disabled={busy}
            class="flex-1 min-w-0 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
          />
        </form>
      )}
    </div>
  );
}
